import { ConfigContext } from '@/contextAPI/configContext';
import PolarisIcon from '@/components/_common/PolarisIcon';
import { selectGraphsEditable, selectGraphsStatus } from '@/redux/selectors/dashboardSelectors';
import { savePreferences } from '@/redux/slices/authSlice';
import { cancelGraphChanges, openGraphs, saveGraphChanges, setGraphsEditable, setGraphsStatusCondensed } from '@/redux/slices/dashboardSlice';
import { DashboardState } from '@/redux/slices/slice_types';
import styled from '@emotion/styled';
import { Accordion, AccordionDetails, AccordionSummary, Button, Container, darken } from '@mui/material';
import { useContext } from 'react';
import { buildGraphsOrderChanged, buildGraphsOrderDefault, defaultGraphs, graphDirectory } from '../DefaultGraphs';
import CheckboxItem from './CheckboxItem';
import { CHART_STATUS_LABELS } from '../chartUtils';
import ResetButton from '@/components/_common/IconButtons/ResetButton';
import SlideToggle from '@/components/_common/SlideToggle';
import { baseGraphList } from '@/redux/selectors/configSelectors';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { PLACEHOLDER_CHART } from '../Widgets/WidgetFrame';

const CustomizeView = () => {
  const dispatch = useAppDispatch();
  const status = useAppSelector(selectGraphsStatus);
  const dashboardConfig = useContext(ConfigContext)?.dashboardConfig;
  const defaultStatusCards = dashboardConfig.statusCards;
  const graphList = useContext(ConfigContext)?.dashboardConfig?.graphList || defaultGraphs;
  const editable: DashboardState['graphs']['editable'] = useAppSelector(selectGraphsEditable);

  const toggleGraphCheck = (id: string) => {
    const updatedList = editable.list.map((listItem) => (listItem.id === id ? { ...listItem, checked: !listItem.checked } : listItem));

    // use orderBuildType to determine how changes should be made to the order
    // if default: build checked graphs in order of list
    // if custom: filter out unchecked graphs, and add new charts to end
    const updatedOrder =
      editable.orderBuildType === 'default' ? buildGraphsOrderDefault(updatedList) : buildGraphsOrderChanged(updatedList, editable.order);

    dispatch(
      setGraphsEditable({
        ...editable,
        list: updatedList,
        order: updatedOrder
      })
    );
  };

  const handleCancel = () => {
    dispatch(cancelGraphChanges());
  };

  const handleSave = () => {
    dispatch(saveGraphChanges());
    dispatch(
      savePreferences({
        dashboard: {
          ...editable,
          order: editable.order.filter((row) => {
            if (row.length === 1 && row[0] === PLACEHOLDER_CHART) {
              return false;
            }

            return row.length > 0;
          })
        }
      })
    );
  };

  const handleReset = () => {
    const allGraphs = baseGraphList.concat(graphList);
    const newGraph = {
      list: allGraphs,
      order: buildGraphsOrderDefault(allGraphs),
      orderBuildType: 'default' as DashboardState['graphs']['readOnly']['orderBuildType'],
      statusCards: defaultStatusCards || CHART_STATUS_LABELS
    };

    dispatch(setGraphsEditable(newGraph));
  };

  return (
    <Wrapper editable={+status.editable}>
      <Accordion expanded={status.editable} disableGutters>
        <StyledAccordionSummary>
          {status.editable ? (
            <EditableButtons>
              <Button onClick={handleCancel} variant="text" color="default">
                Cancel
              </Button>
              <Button onClick={handleSave} variant="contained" color="primary">
                Save
              </Button>
            </EditableButtons>
          ) : (
            <CustomizeButton disabled={!Object.keys(editable.list).length} color="default" variant="text" onClick={() => dispatch(openGraphs())}>
              Customize
              <CogIcon icon="settings_filled" />
            </CustomizeButton>
          )}
        </StyledAccordionSummary>
        <StyledAccordionDetails>
          <Content>
            <CheckboxList>
              {editable.list
                .filter((item) => !item.fullRow)
                .map((listItem) => (
                  <CheckboxItem
                    id={listItem.id}
                    key={listItem.id}
                    label={graphDirectory[listItem.id as keyof typeof graphDirectory].label}
                    disabled={false}
                    checked={listItem.checked}
                    handleChange={(e: any) => toggleGraphCheck(e.target.id)}
                    sx={{ mr: 1.5 }}
                  />
                ))}
            </CheckboxList>
            <ActionButtons>
              <SlideToggle
                left={{ label: <PolarisIcon icon="graph" />, value: false }}
                right={{ label: <PolarisIcon icon="menu" />, value: true }}
                defaultUnchecked={!status.condensed}
                callback={(value: string | boolean) => dispatch(setGraphsStatusCondensed(value as boolean))}
                alternateColors
              />
              <ResetButton onClick={handleReset} />
            </ActionButtons>
          </Content>
        </StyledAccordionDetails>
      </Accordion>
    </Wrapper>
  );
};

export default CustomizeView;

const Wrapper = styled(Container)<{ editable: number }>`
  width: 100%;
  margin-bottom: ${({ theme, editable }) => theme.spacing(editable ? 3 : 1.5)};
  transition: margin 0.3s ease-in-out;

  .MuiAccordion-root {
    box-shadow: none;
    border: none;
  }

  .MuiPaper-root.MuiPaper-elevation {
    box-shadow: none;
    border: none;
  }
`;

const StyledAccordionSummary = styled(AccordionSummary)`
  background: ${({ theme }) => theme.palette.background.default};
  transition: background 0s;
  cursor: default !important;

  .MuiAccordionSummary-content {
    padding-bottom: ${({ theme }) => theme.spacing(1)};
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }
`;

const StyledAccordionDetails = styled(AccordionDetails)`
  background: ${({ theme }) => theme.palette.background.paper};

  .MuiAccordionDetails-root {
    margin: 0px;
  }
`;

const Content = styled.div`
  padding: ${({ theme }) => theme.spacing(2)};
  display: flex;
  flex-direction: row;
  white-space: nowrap;
`;

const CheckboxList = styled.div`
  display: flex;
  flex-grow: 2;
  align-items: flex-start;
  flex-wrap: wrap;
  width: 105px;

  .MuiTypography-root {
    font-size: 1.1rem;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  flex-direction: row;
  column-gap: ${({ theme }) => theme.spacing(1.5)};
  height: 35px;
`;

const CustomizeButton = styled(Button)`
  font-size: 1.1rem;
  white-space: nowrap;
  column-gap: ${({ theme }) => theme.spacing(1)};

  ${({ theme }) =>
    theme.palette.mode === 'light' &&
    `
  &:hover {
    background: ${darken(theme.palette.background.default, 0.05)};
  }
  `}
`;

const CogIcon = styled(PolarisIcon)`
  height: 15px;
  width: 15px;
`;

const EditableButtons = styled.div`
  display: flex;
  column-gap: ${({ theme }) => theme.spacing(1.5)};
`;
